import React from "react";
import { Typography } from "antd";
import {
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    StopOutlined,
    CreditCardOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { SUBSCRIPTION_STATUS, ROUTES } from "../../constants";
import "./SubscriptionBlockedPage.scss";

const { Title, Paragraph } = Typography;

type Props = {
    status: string;
};

type StatusConfig = {
    title: string;
    description: string;
    accent: "orange" | "red" | "blue";
    icon: React.ReactNode;
};

const statusMessages: Record<string, StatusConfig> = {
    [SUBSCRIPTION_STATUS.PAST_DUE]: {
        title: "Payment Failed",
        description:
            "Your last payment was unsuccessful. Please visit the Billing page to update your payment method and restore access.",
        accent: "orange",
        icon: <ExclamationCircleOutlined />,
    },
    [SUBSCRIPTION_STATUS.CANCELED]: {
        title: "Subscription Canceled",
        description:
            "Your subscription has been canceled. Head to Billing to add a payment method and reactivate your plan.",
        accent: "red",
        icon: <StopOutlined />,
    },
    [SUBSCRIPTION_STATUS.INCOMPLETE]: {
        title: "Setup Incomplete",
        description:
            "Your subscription setup is incomplete. Visit the Billing page to add a payment method and activate your account.",
        accent: "red",
        icon: <CreditCardOutlined />,
    },
    [SUBSCRIPTION_STATUS.PAUSED]: {
        title: "Trial Ended",
        description:
            "Your free trial has ended. Go to Billing to activate your subscription and continue using the platform.",
        accent: "orange",
        icon: <ClockCircleOutlined />,
    },
    [SUBSCRIPTION_STATUS.NOT_INITIALIZED]: {
        title: "No Active Subscription",
        description:
            "You don't have an active subscription. Visit Billing or contact support if you believe this is an error.",
        accent: "blue",
        icon: <ExclamationCircleOutlined />,
    },
};

const defaultConfig: StatusConfig = {
    title: "Subscription Inactive",
    description:
        "Your subscription is currently inactive. Please visit the Billing page to update your payment details.",
    accent: "orange",
    icon: <ExclamationCircleOutlined />,
};

const pillLabel: Record<string, string> = {
    [SUBSCRIPTION_STATUS.PAST_DUE]: "Past Due",
    [SUBSCRIPTION_STATUS.CANCELED]: "Canceled",
    [SUBSCRIPTION_STATUS.INCOMPLETE]: "Incomplete",
    [SUBSCRIPTION_STATUS.PAUSED]: "Trial Ended",
    [SUBSCRIPTION_STATUS.NOT_INITIALIZED]: "Not Initialized",
};

const SubscriptionBlockedPage = ({ status }: Props) => {
    const navigate = useNavigate();
    const { title, description, accent, icon } =
        statusMessages[status] ?? defaultConfig;

    return (
        <div className="sub-blocked">
            <div className="sub-blocked__card">
                <div className={`sub-blocked__glow sub-blocked__glow--${accent}`} />

                <div className={`sub-blocked__icon-wrap sub-blocked__icon-wrap--${accent}`}>
                    <span className="sub-blocked__icon">{icon}</span>
                </div>

                <div className={`sub-blocked__pill sub-blocked__pill--${accent}`}>
                    <span className="sub-blocked__pill-dot" />
                    {pillLabel[status] ?? status.replace(/_/g, " ")}
                </div>

                <Title className="sub-blocked__title">{title}</Title>

                <Paragraph className="sub-blocked__desc">{description}</Paragraph>

                <div className="sub-blocked__divider" />

                <div className="sub-blocked__actions">
                    <button
                        className="sub-blocked__btn-primary"
                        onClick={() => navigate(ROUTES.BILLING)}
                    >
                        Go to Billing
                        <ArrowRightOutlined style={{ fontSize: 13 }} />
                    </button>
                </div>

                <p className="sub-blocked__footer">
                    Need help? Contact our support team.
                </p>
            </div>
        </div>
    );
};

export default SubscriptionBlockedPage;
